import { useZero } from "@rocicorp/zero/react";
import { useMemo, useState } from "react";
import { sharedMutators } from "../../../shared/mutators";
import { queries } from "../../../shared/queries";
import { useHasAccess } from "../../hooks/useHasAccess";
import { useQuery } from "../../hooks/useQuery";
import { useSpaceDisplayName } from "../../hooks/useSpaceDisplayName";
import { spaceIcon } from "../../lib/spaceIcon";
import {
  FormCard,
  IconContainer,
  RowLabel,
  SectionHeading,
  SpendList,
  SpendRow,
  UsdInput,
} from "../AiProviderModal/modalForm";
import { Icon, type IconName } from "../Icon";
import { Text } from "../Text";

const TOTAL = "total";
const inputStyle = { width: 160 } as const;

type ScopeRow = {
  key: string;
  spaceId: string | null;
  label: string;
  icon: IconName;
};

type Props = {
  connectorId: string;
};

export function UsageLimitsSection({ connectorId }: Props) {
  const z = useZero();
  const canManage = useHasAccess("tenant:ai_providers_manage");
  const [budgets] = useQuery(queries.monitoringUsageBudgets());
  const [spaces] = useQuery(queries.spacesLite({ includeAdminSpaces: true }));
  const displaySpaceName = useSpaceDisplayName();
  const [drafts, setDrafts] = useState<Record<string, string>>({});

  const scopeRows = useMemo<ScopeRow[]>(() => {
    const teamSpaces = (spaces ?? [])
      .filter((space) => space.mode !== "personal")
      .map((space) => ({
        key: space.id,
        spaceId: space.id as string | null,
        label: displaySpaceName(space),
        icon: spaceIcon(space.mode),
      }))
      .sort((a, b) => a.label.localeCompare(b.label));
    return [
      {
        key: TOTAL,
        spaceId: null,
        label: "Total spend",
        icon: "wallet" as const,
      },
      ...teamSpaces,
    ];
  }, [spaces, displaySpaceName]);

  const budgetByScope = useMemo(() => {
    const map = new Map<string, { id: string; limitAmount: number }>();
    for (const budget of budgets ?? []) {
      if (budget.connectorId !== connectorId) continue;
      if (budget.measure !== "spend_usd") continue;
      map.set(budget.spaceId ?? TOTAL, budget);
    }
    return map;
  }, [budgets, connectorId]);

  const valueFor = (scope: ScopeRow) => {
    const draft = drafts[scope.key];
    if (draft != null) return draft;
    const limit = budgetByScope.get(scope.key)?.limitAmount;
    return limit == null ? "" : String(limit);
  };

  const commit = (scope: ScopeRow) => {
    if (!canManage) return;
    const budget = budgetByScope.get(scope.key);
    const raw = valueFor(scope).trim();
    const amount = raw === "" ? 0 : Number.parseFloat(raw);
    if (!Number.isFinite(amount)) return;
    if (amount <= 0) {
      if (budget) z.mutate(sharedMutators.deleteUsageBudget({ id: budget.id }));
      return;
    }
    if (budget?.limitAmount === amount) return;
    z.mutate(
      sharedMutators.updateUsageBudget({
        connectorId,
        ...(scope.spaceId ? { spaceId: scope.spaceId } : {}),
        measure: "spend_usd",
        limitAmount: amount,
      })
    );
  };

  return (
    <FormCard>
      <SectionHeading
        title="Spend notifications"
        description="Warns in Monitoring when this connector’s spend over the last 30 days reaches an amount — for the total and per space. Empty means no notification; requests are never blocked."
      />
      <SpendList>
        {scopeRows.map((scope) => (
          <SpendRow key={scope.key} onBlur={() => commit(scope)}>
            <IconContainer>
              <Icon name={scope.icon} size={16} />
            </IconContainer>
            <RowLabel>
              <Text
                variant="text-regular-md"
                color="var(--text-primary)"
                ellipsis
              >
                {scope.label}
              </Text>
            </RowLabel>
            <UsdInput
              value={valueFor(scope)}
              placeholder="0.00"
              disabled={!canManage}
              onChange={(next) =>
                setDrafts((prev) => ({ ...prev, [scope.key]: next }))
              }
              style={inputStyle}
            />
          </SpendRow>
        ))}
      </SpendList>
    </FormCard>
  );
}
